import { useState } from "react";
import { X } from "lucide-react";

// ── Paste your Google Apps Script Web App URL here ────────────────────────────
const CONTACT_SHEET_URL = "https://script.google.com/macros/s/AKfycbw4WkH2CqSLHv63eFmGubKJmOrbIghcoL5YX26Tsc9szhNwxAxTzS4JWHNYV6KkgMHh/exec";
// ─────────────────────────────────────────────────────────────────────────────

export default function ContactModal({ onClose }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", email: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState("");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.phone || !form.email || !form.message) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        type:      "Contact Form",
        firstName: form.firstName,
        lastName:  form.lastName,
        phone:     form.phone,
        email:     form.email,
        message:   form.message,
        timestamp: new Date().toISOString(),
      });
      await fetch(`${CONTACT_SHEET_URL}?${params.toString()}`, {
        method: "GET", mode: "no-cors",
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes contactFadeIn {
          from { opacity: 0; transform: scale(0.93) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .contact-input {
          width: 100%;
          padding: 13px 16px;
          border-radius: 10px;
          border: none;
          outline: none;
          font-size: 14px;
          color: #e2e8f0;
          background: rgba(255,255,255,0.07);
          transition: background 0.2s;
          resize: none;
          font-family: inherit;
        }
        .contact-input::placeholder { color: rgba(200,210,230,0.45); }
        .contact-input:focus { background: rgba(255,255,255,0.12); }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="relative w-full max-w-md rounded-3xl overflow-hidden"
          style={{
            animation: "contactFadeIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both",
            background: "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)",
            boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.1)" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
          >
            <X size={16} color="white" />
          </button>

          <div className="px-8 pt-10 pb-8">
            {!submitted ? (
              <>
                {/* Header */}
                <h2 className="text-white text-3xl font-bold text-center mb-1">Hi!</h2>
                <p className="text-center text-sm mb-1" style={{ color: "rgba(180,200,230,0.65)" }}>
                  Write to us at
                </p>
                <p className="text-center text-sm font-semibold text-blue-400 mb-7">
                  info@acespireconsulting.com
                </p>

                {/* Fields */}
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <input
                      className="contact-input"
                      name="firstName"
                      placeholder="First Name"
                      value={form.firstName}
                      onChange={handleChange}
                    />
                    <input
                      className="contact-input"
                      name="lastName"
                      placeholder="Last Name"
                      value={form.lastName}
                      onChange={handleChange}
                    />
                  </div>
                  <input
                    className="contact-input"
                    name="phone"
                    placeholder="Phone Number"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                  />
                  <input
                    className="contact-input"
                    name="email"
                    placeholder="Email Address"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                  <textarea
                    className="contact-input"
                    name="message"
                    placeholder="Your message…"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-xs mt-3 text-center">{error}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full mt-6 py-4 rounded-xl text-gray-900 font-bold text-sm transition-opacity duration-200"
                  style={{
                    background: submitting ? "#e2e8f0" : "#ffffff",
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? "not-allowed" : "pointer",
                    border: "none",
                  }}
                >
                  {submitting ? "Sending…" : "Submit"}
                </button>
              </>
            ) : (
              /* Success */
              <div className="text-center py-8">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: "rgba(37,99,235,0.2)" }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="text-white text-2xl font-bold mb-2">Message Sent!</h3>
                <p className="text-sm" style={{ color: "rgba(180,200,230,0.6)" }}>
                  Thanks for reaching out. We'll get back to you shortly.</p>
                <button
                  onClick={onClose}
                  className="mt-8 px-8 py-3 rounded-xl text-white text-sm font-semibold"
                  style={{ background: "#2563eb", border: "none", cursor: "pointer" }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}